// Optimized Users.jsx with server-side pagination
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  Users as UsersIcon,
  Filter,
  Download,
  RefreshCw,
  UserCheck,
  UserX,
  Phone,
  PhoneOff,
  Building,
  MapPin,
  TrendingUp,
  TrendingDown,
  Loader2,
  GitBranch,
  Coins,
  MessageSquare,
  KeyRound,
  Eye,
  EyeOff,
  ShieldAlert,
  X
} from 'lucide-react';
import { ADMIN_SET_PASSWORD } from '../../api/users-api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Import components
import DataTableSettings from '../../components/common/DataTableSettings';
import ModalComponent from '../../components/common/ModalComponent';
import UserDetailModal from '../../components/admin/users/UserDetailModal';
import { toast } from 'react-toastify';

const GET_USERS_STATS = gql`
  query GetUsersStatistics {
    usersStatistics {
      total
      active
      inactive
      verified
      byType {
        PHARMACIST
        LABORATORY
        SELLER
      }
    }
  }
`;

// ✅ OPTIMIZED: Paginated GraphQL Queries
const GET_USERS_PAGINATED = gql`
  query GetUsersPaginated($usersQueryInput: UsersQueryInput) {
    usersPaginated(usersQueryInput: $usersQueryInput) {
      users {
        id
        email
        firstName
        lastName
        tel
        userType
        subType
        referralCode
        referredUsersCount
        referralEarnings
        commissionBalance
        isActive
        isPhoneVerified
        lastLogin
        createdAt
        pharmacy {
          id
          namePharmacy
          telPharmacy
          commissionBalance
          regionId
          villeId
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedPharmacy {
          id
          namePharmacy
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        ownedLaboratory {
          id
          nom
        }
        laboratories {
          id
          nom
        }
        transactions {
          id
          status
        }
        paymentRequests {
          id
          status
        }
        labels {
          id
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Keep lightweight queries for dropdowns
const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      nomRegion
      villeVillages {
        id
        nomVilleVillage
      }
    }
  }
`;

const GET_VILLES_BY_REGION = gql`
  query GetVillesByRegion($regionId: ID!) {
    villesByRegion(regionId: $regionId) {
      id
      nomVilleVillage
    }
  }
`;

const TOGGLE_USER_ACTIVE = gql`
  mutation ToggleUserActive($id: ID!, $isActive: Boolean!) {
    toggleUserActive(id: $id, isActive: $isActive) {
      id
      isActive
    }
  }
`;

const BLOCK_USERS_WITH_MESSAGE = gql`
  mutation BlockUsersWithMessage($userIds: [ID!]!, $message: String!) {
    blockUsersWithMessage(userIds: $userIds, message: $message)
  }
`;

const Users = () => {
  const navigate = useNavigate();

  // ✅ PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // ✅ FILTER STATE
  const [filters, setFilters] = useState({
    userType: '',
    subType: '',
    isActive: '',
    isPhoneVerified: '',
    regionId: '',
    villeId: '',
    laboratoryId: ''
  });

  // UI State
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedUserForDetail, setSelectedUserForDetail] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [blockModal, setBlockModal] = useState({ open: false, user: null });
  const [pwdModal, setPwdModal] = useState({ open: false, user: null });
  const [blockMsgModal, setBlockMsgModal] = useState({ open: false, userIds: [], names: '' });
  const [blockMsgText, setBlockMsgText] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const [isExporting, setIsExporting] = useState(false);

  const [fetchAllForExport] = useLazyQuery(GET_USERS_PAGINATED, {
    fetchPolicy: 'network-only',
  });

  const [adminSetPassword, { loading: settingPassword }] = useMutation(ADMIN_SET_PASSWORD, {
    onCompleted: (data) => {
      if (data.adminSetPassword) {
        toast.success('Mot de passe modifié avec succès');
      } else {
        toast.error('Erreur lors du changement de mot de passe');
      }
      setPwdModal({ open: false, user: null });
      setNewPassword('');
      setShowPwd(false);
    },
    onError: (err) => toast.error(`Erreur: ${err.message}`)
  });

  const [toggleUserActive, { loading: togglingUser }] = useMutation(TOGGLE_USER_ACTIVE, {
    refetchQueries: ['GetUsersPaginated'],
    onCompleted: (data) => {
      const action = data.toggleUserActive.isActive ? 'débloqué' : 'bloqué';
      toast.success(`Utilisateur ${action} avec succès`);
      setBlockModal({ open: false, user: null });
    },
    onError: (err) => toast.error(`Erreur: ${err.message}`)
  });

  const [blockUsersWithMessage, { loading: blockingUsers }] = useMutation(BLOCK_USERS_WITH_MESSAGE, {
    refetchQueries: ['GetUsersPaginated'],
    onCompleted: () => {
      toast.success(`${blockMsgModal.userIds.length} utilisateur(s) bloqué(s) avec succès`);
      setBlockMsgModal({ open: false, userIds: [], names: '' });
      setBlockMsgText('');
    },
    onError: (err) => toast.error(`Erreur: ${err.message}`)
  });

  // ✅ OPTIMIZED: Paginated users query
  const { 
    data: usersData, 
    loading: usersLoading, 
    error: usersError,
    refetch: refetchUsers 
  } = useQuery(GET_USERS_PAGINATED, {
    variables: {
      usersQueryInput: {
        page: currentPage,
        limit: itemsPerPage,
        searchTerm: searchTerm || undefined,
        sortBy,
        sortOrder,
        userType: filters.userType || undefined,
        subType: filters.subType || undefined,
        regionId: filters.regionId || undefined,
        isActive: filters.isActive !== '' ? filters.isActive === 'true' : undefined,
        isPhoneVerified: filters.isPhoneVerified !== '' ? filters.isPhoneVerified === 'true' : undefined,
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  const { data: usersStatsData } = useQuery(GET_USERS_STATS, { fetchPolicy: 'cache-and-network' });
  const usersStats = usersStatsData?.usersStatistics;

  // Lightweight queries for dropdowns
  const {
    data: regionsData,
    loading: regionsLoading
  } = useQuery(GET_REGIONS);

  const { 
    data: villesData,
    loading: villesLoading
  } = useQuery(GET_VILLES_BY_REGION, {
    variables: { regionId: filters.regionId || 'dummy' },
    skip: !filters.regionId,
    fetchPolicy: 'cache-and-network'
  });

  // ✅ DEBOUNCED SEARCH
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // ✅ OPTIMIZED: Extract data from paginated response
  const users = usersData?.usersPaginated?.users || [];
  const pagination = usersData?.usersPaginated?.pagination;
  const regions = regionsData?.regions || [];
  const villes = villesData?.villesByRegion || [];

  // ✅ PAGINATION HANDLERS
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleSearch = (search) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  // ✅ FILTER HANDLERS
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [filterName]: value };
      
      // Reset ville when region changes
      if (filterName === 'regionId') {
        newFilters.villeId = '';
      }
      
      return newFilters;
    });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      userType: '',
      subType: '',
      isActive: '',
      isPhoneVerified: '',
      regionId: '',
      villeId: '',
      laboratoryId: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // User detail handlers
  const handleUserRowClick = (user) => {
    setSelectedUserForDetail(user);
    setShowUserDetailModal(true);
  };

  const handleRefresh = () => {
    refetchUsers();
    toast.success('Données actualisées');
  };

  // ✅ EXPORT FUNCTIONALITY
  const handleExportUsers = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { data } = await fetchAllForExport({
        variables: {
          usersQueryInput: {
            page: 1,
            limit: pagination?.totalItems || 10000,
            searchTerm: searchTerm || undefined,
            sortBy,
            sortOrder,
            userType: filters.userType || undefined,
            subType: filters.subType || undefined,
            regionId: filters.regionId || undefined,
            isActive: filters.isActive !== '' ? filters.isActive === 'true' : undefined,
            isPhoneVerified: filters.isPhoneVerified !== '' ? filters.isPhoneVerified === 'true' : undefined,
          }
        }
      });

      const allUsers = data?.usersPaginated?.users || [];

      const headers = [
        'Email', 'Prénom', 'Nom', 'Téléphone', 'Type', 'Actif',
        'Téléphone vérifié', 'Région', 'Ville', 'Pharmacie', 'Laboratoire',
        'Commission (DH)', 'Date de création'
      ];

      const csvContent = [
        headers.map(h => `"${h}"`).join(','),
        ...allUsers.map(user => [
          user.email,
          user.firstName || '',
          user.lastName || '',
          user.tel || '',
          user.userType,
          user.isActive ? 'Oui' : 'Non',
          user.isPhoneVerified ? 'Oui' : 'Non',
          user.pharmacy?.region?.nomRegion || user.ownedPharmacy?.region?.nomRegion || '',
          user.pharmacy?.ville?.nomVilleVillage || user.ownedPharmacy?.ville?.nomVilleVillage || '',
          user.pharmacy?.namePharmacy || user.ownedPharmacy?.namePharmacy || '',
          user.ownedLaboratory?.nom || user.laboratories?.[0]?.nom || '',
          user.commissionBalance || 0,
          format(new Date(user.createdAt), 'dd/MM/yyyy')
        ].map(f => `"${String(f).replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Export terminé — ${allUsers.length} utilisateurs`);
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setIsExporting(false);
    }
  };

  // ✅ TABLE COLUMNS CONFIGURATION
  const columns = [
    {
      Header: 'Utilisateur',
      accessor: 'email',
      sortable: true,
      searchable: true,
      Cell: ({ row }) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
              <UsersIcon size={16} className="text-gray-600" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {row.firstName && row.lastName 
                ? `${row.firstName} ${row.lastName}`
                : row.email}
            </div>
            <div className="text-sm text-gray-500 truncate">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      Header: 'Type',
      accessor: 'userType',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => {
        const typeColors = {
          ADMIN: 'bg-purple-100 text-purple-800',
          PHARMACIST: 'bg-green-100 text-green-800',
          LABORATORY: 'bg-blue-100 text-blue-800',
          SELLER: 'bg-orange-100 text-orange-800',
          AGENT_TELEPHONIQUE: 'bg-yellow-100 text-yellow-800',
          RESPONSABLE_ACHAT: 'bg-pink-100 text-pink-800',
        };
        const typeLabels = {
          ADMIN: 'Admin',
          PHARMACIST: 'Pharmacien',
          LABORATORY: 'Laboratoire',
          SELLER: 'Vendeur',
          AGENT_TELEPHONIQUE: 'Agent tél.',
          RESPONSABLE_ACHAT: 'Resp. achat',
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {typeLabels[value] || value}
          </span>
        );
      }
    },
    {
      Header: 'Statut',
      accessor: 'isActive',
      sortable: true,
      searchable: false,
      Cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            {row.isActive ? (
              <UserCheck size={16} className="text-green-500" />
            ) : (
              <UserX size={16} className="text-red-500" />
            )}
            <span className={`ml-1 text-xs ${row.isActive ? 'text-green-700' : 'text-red-700'}`}>
              {row.isActive ? 'Actif' : 'Inactif'}
            </span>
          </div>
          <div className="flex items-center">
            {row.isPhoneVerified ? (
              <Phone size={14} className="text-blue-500" />
            ) : (
              <PhoneOff size={14} className="text-gray-400" />
            )}
            <span className={`ml-1 text-xs ${row.isPhoneVerified ? 'text-blue-700' : 'text-gray-500'}`}>
              {row.isPhoneVerified ? 'Vérifié' : 'Non vérifié'}
            </span>
          </div>
        </div>
      )
    },
    {
      Header: 'Localisation',
      accessor: 'region',
      sortable: true,
      searchable: true,
      Cell: ({ row }) => {
        const region = row.pharmacy?.region || row.ownedPharmacy?.region;
        const ville = row.pharmacy?.ville || row.ownedPharmacy?.ville;
        
        if (!region && !ville) return <span className="text-gray-400">-</span>;
        
        return (
          <div className="text-sm">
            <div className="flex items-center text-gray-900">
              <MapPin size={14} className="mr-1 text-gray-400" />
              {region?.nomRegion || '-'}
            </div>
            {ville && (
              <div className="text-gray-500 ml-5">{ville.nomVilleVillage}</div>
            )}
          </div>
        );
      }
    },
    {
      Header: 'Établissement',
      accessor: 'establishment',
      sortable: false,
      searchable: true,
      Cell: ({ row }) => {
        const pharmacy = row.pharmacy || row.ownedPharmacy;
        const laboratory = row.ownedLaboratory || row.laboratories?.[0];
        
        if (pharmacy) {
          return (
            <div className="flex items-center">
              <Building size={14} className="mr-2 text-green-500" />
              <span className="text-sm text-gray-900">{pharmacy.namePharmacy}</span>
            </div>
          );
        }
        
        if (laboratory) {
          return (
            <div className="flex items-center">
              <Building size={14} className="mr-2 text-blue-500" />
              <span className="text-sm text-gray-900">{laboratory.nom}</span>
            </div>
          );
        }
        
        return <span className="text-gray-400">-</span>;
      }
    },
    {
      Header: 'Cagnotte (DH)',
      accessor: 'commissionBalance',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className={`font-medium ${value > 0 ? 'text-blue-600' : 'text-gray-500'}`}>
          {(value || 0).toFixed(2)} DH
        </span>
      )
    },
    {
      Header: 'Filleuls',
      accessor: 'referredUsersCount',
      sortable: false,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center space-x-1">
          <TrendingUp size={14} className={value > 0 ? 'text-green-500' : 'text-gray-300'} />
          <span className={`font-medium text-sm ${value > 0 ? 'text-green-700' : 'text-gray-400'}`}>
            {value || 0}
          </span>
        </div>
      )
    },
    {
      Header: 'Gains parrainage',
      accessor: 'referralEarnings',
      sortable: false,
      searchable: false,
      Cell: ({ value }) => (
        <span className={`font-medium text-sm ${value > 0 ? 'text-green-600' : 'text-gray-400'}`}>
          {value > 0 ? `${(value || 0).toFixed(2)} DH` : '—'}
        </span>
      )
    },
    {
      Header: 'Activité',
      accessor: 'stats',
      sortable: false,
      searchable: false,
      Cell: ({ row }) => {
        const transactionCount = row.transactions?.length || 0;
        const paymentRequestCount = row.paymentRequests?.filter(pr => pr.status === 'VALIDATED')?.length || 0;
        
        return (
          <div className="text-sm">
            <div className="flex items-center">
              <TrendingUp size={14} className="mr-1 text-green-500" />
              <span>{transactionCount} transactions</span>
            </div>
            <div className="flex items-center text-gray-500">
              <TrendingDown size={14} className="mr-1" />
              <span>{paymentRequestCount} paiements</span>
            </div>
          </div>
        );
      }
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      sortable: false,
      searchable: false,
      Cell: ({ row }) => (
        <div className="flex items-center space-x-1">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/messages?userId=${row.id}`); }}
            className="inline-flex items-center p-1.5 rounded-md text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 transition-colors"
            title="Envoyer un message"
          >
            <MessageSquare size={13} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setPwdModal({ open: true, user: row }); setNewPassword(''); setShowPwd(false); }}
            className="inline-flex items-center p-1.5 rounded-md text-xs font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 transition-colors"
            title="Forcer le changement de mot de passe"
          >
            <KeyRound size={13} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setBlockModal({ open: true, user: row }); }}
            className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              row.isActive
                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                : 'bg-green-100 text-green-700 hover:bg-green-200'
            }`}
          >
            {row.isActive ? (
              <><UserX size={13} className="mr-1" />Bloquer</>
            ) : (
              <><UserCheck size={13} className="mr-1" />Débloquer</>
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.tel || row.email;
              setBlockMsgModal({ open: true, userIds: [row.id], names: name });
              setBlockMsgText('');
            }}
            className="inline-flex items-center p-1.5 rounded-md text-xs font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 transition-colors"
            title="Bloquer avec message personnalisé"
          >
            <ShieldAlert size={13} />
          </button>
        </div>
      )
    },
    {
      Header: 'Dernière connexion',
      accessor: 'lastLogin',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => {
        if (!value) return <span className="text-gray-400">Jamais</span>;
        
        const lastLogin = new Date(value);
        const now = new Date();
        const diffDays = Math.floor((now - lastLogin) / (1000 * 60 * 60 * 24));
        
        return (
          <div className="text-sm">
            <div className="text-gray-900">
              {format(lastLogin, 'dd/MM/yyyy')}
            </div>
            <div className={`text-xs ${diffDays <= 7 ? 'text-green-600' : diffDays <= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
              {diffDays === 0 ? 'Aujourd\'hui' : 
               diffDays === 1 ? 'Hier' : 
               `Il y a ${diffDays} jours`}
            </div>
          </div>
        );
      }
    }
  ];

  // ✅ OPTIMIZED: Pass pagination data to DataTableSettings
  const tableProps = {
    title: "Gestion des Utilisateurs",
    subtitle: `${pagination?.totalItems || 0} utilisateur(s) au total`,
    data: users,
    columns,
    onRowClick: handleUserRowClick,
    rowClickEnabled: true,
    loading: usersLoading,
    emptyMessage: "Aucun utilisateur trouvé",
    // ✅ NEW: Pass pagination props
    pagination: {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
      totalItems: pagination?.totalItems || 0,
      itemsPerPage: pagination?.itemsPerPage || itemsPerPage,
      hasNextPage: pagination?.hasNextPage || false,
      hasPreviousPage: pagination?.hasPreviousPage || false,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange
    },
    // ✅ NEW: Pass search and sort props
    search: {
      value: searchTerm,
      onChange: handleSearch,
      placeholder: "Rechercher par nom, email, téléphone..."
    },
    sort: {
      column: sortBy,
      direction: sortOrder,
      onSort: handleSort
    }
  };

  const isLoading = usersLoading || regionsLoading;

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {usersError && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 text-red-500 font-bold mr-3">⚠</div>
            <div>
              <p className="text-sm font-medium text-red-800">Erreur lors du chargement des utilisateurs</p>
              <p className="text-xs text-red-600 mt-1 font-mono">{usersError.message}</p>
              {usersError.graphQLErrors?.map((e, i) => (
                <p key={i} className="text-xs text-red-600 font-mono">→ {e.message}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez les utilisateurs de la plateforme
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 size={16} className="mr-2 animate-spin" />
            ) : (
              <RefreshCw size={16} className="mr-2" />
            )}
            Actualiser
          </button>
          <button
            onClick={handleExportUsers}
            disabled={isExporting || (pagination?.totalItems === 0)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={16} className={`mr-2 ${isExporting ? 'animate-bounce' : ''}`} />
            {isExporting ? 'Export...' : 'Exporter CSV'}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {usersStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-gray-500 mb-0.5">Total</p>
            <p className="text-2xl font-bold text-gray-900">{usersStats.total}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-green-600 mb-0.5">Actifs</p>
            <p className="text-2xl font-bold text-green-800">{usersStats.active}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-emerald-600 mb-0.5">Pharmaciens</p>
            <p className="text-2xl font-bold text-emerald-800">{usersStats.byType?.PHARMACIST ?? 0}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-blue-600 mb-0.5">Laboratoires</p>
            <p className="text-2xl font-bold text-blue-800">{usersStats.byType?.LABORATORY ?? 0}</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-orange-600 mb-0.5">Vendeurs</p>
            <p className="text-2xl font-bold text-orange-800">{usersStats.byType?.SELLER ?? 0}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 shadow-sm">
            <p className="text-xs text-red-600 mb-0.5">Inactifs</p>
            <p className="text-2xl font-bold text-red-800">{usersStats.inactive}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filtres</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="sm:hidden inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <Filter size={16} className="mr-2" />
              Filtres
            </button>
            <button
              onClick={handleResetFilters}
              className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${showMobileFilters ? 'block' : 'hidden sm:grid'}`}>
          {/* User Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type d'utilisateur
            </label>
            <select
              value={filters.userType}
              onChange={(e) => handleFilterChange('userType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les types</option>
              <option value="ADMIN">Admin</option>
              <option value="PHARMACIST">Pharmacien</option>
              <option value="LABORATORY">Laboratoire</option>
              <option value="SELLER">Vendeur</option>
              <option value="AGENT_TELEPHONIQUE">Agent téléphonique</option>
              <option value="RESPONSABLE_ACHAT">Responsable d'achat</option>
            </select>
          </div>

          {/* Active Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={filters.isActive}
              onChange={(e) => handleFilterChange('isActive', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="true">Actif</option>
              <option value="false">Inactif</option>
            </select>
          </div>

          {/* Phone Verification Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone vérifié
            </label>
            <select
              value={filters.isPhoneVerified}
              onChange={(e) => handleFilterChange('isPhoneVerified', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous</option>
              <option value="true">Vérifié</option>
              <option value="false">Non vérifié</option>
            </select>
          </div>

          {/* Region Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Région
            </label>
            <select
              value={filters.regionId}
              onChange={(e) => handleFilterChange('regionId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les régions</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.nomRegion}</option>
              ))}
            </select>
          </div>

          {/* Ville Filter (only show if region selected) */}
          {filters.regionId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville
              </label>
              <select
                value={filters.villeId}
                onChange={(e) => handleFilterChange('villeId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={villesLoading}
              >
                <option value="">Toutes les villes</option>
                {villes.map(ville => (
                  <option key={ville.id} value={ville.id}>{ville.nomVilleVillage}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {(filters.userType || filters.isActive !== '' || filters.isPhoneVerified !== '' || filters.regionId || searchTerm) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {filters.userType && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Type: {filters.userType}
                <button
                  onClick={() => handleFilterChange('userType', '')}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-200 text-blue-600 hover:bg-blue-300"
                >
                  ×
                </button>
              </span>
            )}
            {filters.isActive !== '' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Statut: {filters.isActive === 'true' ? 'Actif' : 'Inactif'}
                <button
                  onClick={() => handleFilterChange('isActive', '')}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-green-200 text-green-600 hover:bg-green-300"
                >
                  ×
                </button>
              </span>
            )}
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Recherche: "{searchTerm}"
                <button
                  onClick={() => handleSearch('')}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ✅ OPTIMIZED: Enhanced DataTableSettings with server-side pagination */}
      <DataTableSettings {...tableProps} />

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUserForDetail}
        isOpen={showUserDetailModal}
        onClose={() => {
          setShowUserDetailModal(false);
          setSelectedUserForDetail(null);
        }}
      />

      {/* Force Password Change Modal */}
      <ModalComponent
        isOpen={pwdModal.open}
        onClose={() => { setPwdModal({ open: false, user: null }); setNewPassword(''); setShowPwd(false); }}
        title="Changer le mot de passe"
        size="sm"
        submitLabel="Confirmer"
        cancelLabel="Annuler"
        onSubmit={() => {
          if (pwdModal.user && newPassword.trim().length >= 6) {
            adminSetPassword({ variables: { id: pwdModal.user.id, newPassword: newPassword.trim() } });
          } else {
            toast.warning('Le mot de passe doit contenir au moins 6 caractères');
          }
        }}
        isSubmitting={settingPassword}
      >
        <div className="py-2 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <KeyRound size={18} className="text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {pwdModal.user?.firstName} {pwdModal.user?.lastName}
              </p>
              <p className="text-xs text-gray-500">{pwdModal.user?.email}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nouveau mot de passe
            </label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
                className="w-full pr-10 pl-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
        </div>
      </ModalComponent>

      {/* Block/Unblock Confirmation Modal */}
      <ModalComponent
        isOpen={blockModal.open}
        onClose={() => setBlockModal({ open: false, user: null })}
        title={blockModal.user?.isActive ? 'Bloquer l\'utilisateur' : 'Débloquer l\'utilisateur'}
        size="sm"
        submitLabel={blockModal.user?.isActive ? 'Bloquer' : 'Débloquer'}
        cancelLabel="Annuler"
        onSubmit={() => {
          if (blockModal.user) {
            toggleUserActive({
              variables: { id: blockModal.user.id, isActive: !blockModal.user.isActive }
            });
          }
        }}
        isSubmitting={togglingUser}
      >
        <div className="py-2">
          {blockModal.user?.isActive ? (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <UserX size={20} className="text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Êtes-vous sûr de vouloir <span className="font-semibold text-red-700">bloquer</span> cet utilisateur ?
                </p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {blockModal.user?.firstName && blockModal.user?.lastName
                    ? `${blockModal.user.firstName} ${blockModal.user.lastName}`
                    : blockModal.user?.email}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  L'utilisateur ne pourra plus se connecter à l'application.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <UserCheck size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-700">
                  Êtes-vous sûr de vouloir <span className="font-semibold text-green-700">débloquer</span> cet utilisateur ?
                </p>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {blockModal.user?.firstName && blockModal.user?.lastName
                    ? `${blockModal.user.firstName} ${blockModal.user.lastName}`
                    : blockModal.user?.email}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  L'utilisateur pourra à nouveau se connecter à l'application.
                </p>
              </div>
            </div>
          )}
        </div>
      </ModalComponent>

      {/* ── Modal Bloquer avec message personnalisé ── */}
      {blockMsgModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldAlert size={20} className="text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Bloquer avec message</h3>
              </div>
              <button onClick={() => setBlockMsgModal({ open: false, userIds: [], names: '' })} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-1">
              Utilisateur(s) concerné(s) :
            </p>
            <p className="text-sm font-medium text-gray-900 mb-4 bg-gray-50 rounded-lg px-3 py-2">
              {blockMsgModal.names}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message affiché à l'utilisateur lors de la connexion
            </label>
            <textarea
              value={blockMsgText}
              onChange={(e) => setBlockMsgText(e.target.value)}
              placeholder="Ex : Votre compte a été suspendu suite à une activité suspecte. Contactez le support."
              rows={4}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
            />

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setBlockMsgModal({ open: false, userIds: [], names: '' })}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                disabled={!blockMsgText.trim() || blockingUsers}
                onClick={() => blockUsersWithMessage({ variables: { userIds: blockMsgModal.userIds, message: blockMsgText.trim() } })}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {blockingUsers ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
                Bloquer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;